import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import ChangePassword from "./changePassword";
import TermsAndConditions from "./termsAndConditions";
import PrivacyPolicy from "./privacyPolicy";
import TextInput from "../../components/textInput";
import CustomButton from "../../components/customButton";
import AppLoader from "../../components/appLoader";
import { openSnackAlert } from "../../helper";
import useAdminSettings from "../../zustand/useAdminSettingsStore";

const AdminSetting = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [workingHours, setWorkingHours] = useState("");
  const [breakPolicies, setBreakPolicies] = useState("");
  const [holidays, setHolidays] = useState("");

  // Zustand store
  const { 
    adminSettings, 
    isLoading, 
    error, 
    updateSettings: updateSettingsStore,
    clearError 
  } = useAdminSettings();

  // Update local state when store data changes
  useEffect(() => {
    if (adminSettings?.organizationSettings) {
      setWorkingHours(adminSettings.organizationSettings.workingHours || "");
      setBreakPolicies(adminSettings.organizationSettings.breakPolicy || "");
      setHolidays(adminSettings.organizationSettings.holidays || "");
    }
  }, [adminSettings]);

  // Show error if any
  useEffect(() => {
    if (error) {
      openSnackAlert(error, "error");
      clearError();
    }
  }, [error, clearError]);

  const handleSaveSettings = async () => {
    if (!adminSettings?._id) {
      return openSnackAlert("Settings not found. Please refresh the page.", "error");
    }

    const settingsData = {
      organizationSettings: {
        ...adminSettings.organizationSettings,
        workingHours,
        breakPolicy: breakPolicies,
        holidays,
      },
    };

    const success = await updateSettingsStore(settingsData);
    
    if (success) {
      openSnackAlert("Settings updated successfully!", "success");
    }
  };

  const tabData = [
    {
      label: "Organization Settings",
      content: (
        <Box>
          <AppLoader isVisible={isLoading} />
          <Typography variant="h5" mb={2}>
            Organizations Settings
          </Typography>

          <Box mb={3} p={2} border="1px solid #225B27" borderRadius="16px">
            <Typography fontWeight={600} mb={1}>
              Working Hours
            </Typography>
            <TextInput
              multiline
              rows={4}
              value={workingHours}
              onChange={(e) => setWorkingHours(e.target.value)}
              placeholder="Enter working hours details..."
              noBorder
            />
          </Box>

          <Box mb={3} p={2} border="1px solid #225B27" borderRadius="16px">
            <Typography fontWeight={600} mb={1}>
              Break Policies
            </Typography>
            <TextInput
              multiline
              rows={4}
              value={breakPolicies}
              onChange={(e) => setBreakPolicies(e.target.value)}
              placeholder="Enter break policies details..."
              noBorder
            />
          </Box>

          <Box mb={3} p={2} border="1px solid #225B27" borderRadius="16px">
            <Typography fontWeight={600} mb={1}>
              Holidays
            </Typography>
            <TextInput
              multiline
              rows={4}
              value={holidays}
              onChange={(e) => setHolidays(e.target.value)}
              placeholder="Enter holidays details..."
              noBorder
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <CustomButton
              btnLabel="Update Settings"
              variant="webbutton"
              handlePressBtn={handleSaveSettings}
            />
          </Box>
        </Box>
      ),
    },
    {
      label: "Terms and Conditions",
      content: <TermsAndConditions />,
    },
    {
      label: "Privacy Policy",
      content: <PrivacyPolicy />,
    },
    {
      label: "Security",
      content: <ChangePassword />,
    },
  ];

  return (
    <Box
      bgcolor="primary.bgLight"
      padding={"20px"}
      borderRadius={"22px"}
      mb={3}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Typography variant="h4">Admin Settings & Controls</Typography>
          <Typography variant="description">
            Customize platform behavior and access
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} md={3}>
          <Box display="flex" flexDirection="column" gap={2}>
            {tabData.map((tab, idx) => (
              <Button
                key={tab.label}
                variant={activeTab === idx ? "contained" : "outlined"}
                onClick={() => setActiveTab(idx)}
                sx={{
                  justifyContent: "flex-start",
                  borderRadius: "22px",
                  background: activeTab === idx ? "#225B27" : "#fff",
                  color: activeTab === idx ? "#fff" : "#225B27",
                  borderColor: "#225B27",
                  fontWeight: 600,
                  fontSize: 14,
                  boxShadow: activeTab === idx ? "0 2px 8px #225B2710" : "none",
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                  "&:hover": {
                    background: activeTab === idx ? "#225B27" : "#f5f5f5",
                  },
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={9}>
          <Box
            border="1.5px solid #225B27"
            borderRadius="22px"
            bgcolor="#fff"
            p={4}
            minHeight="400px"
          >
            {tabData[activeTab].content}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminSetting;
